import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cameraLayoutSchema,
  messageKey,
  cameraProbeResultSchema,
  cameraSchema,
  cameraStreamSchema,
  recorderDetectResultSchema,
  recorderSchema,
  streamKeepaliveSchema,
  streamTicketGrantSchema,
  type Camera,
  type CameraLayout,
  type CameraProbeResult,
  type CameraStream,
  type CreateCameraInput,
  type CreateRecorderInput,
  type Recorder,
  type RecorderDetectResult,
  type SaveLayout,
  type StreamQuality,
  type StreamTicketGrant,
  type UpdateCamera,
  type UpdateRecorder,
} from '@netdeck/contracts';
import { z } from 'zod';
import { API_BASE, apiRequest } from '../../shared/api/client';
import { authStore } from '../../shared/api/auth-store';

const cameraListSchema = z.object({ items: z.array(cameraSchema) });
const layoutListSchema = z.object({ items: z.array(cameraLayoutSchema) });

export const camerasKey = ['cameras'] as const;
export const layoutsKey = ['layouts'] as const;

/**
 * Список камер.
 *
 * Живёт минуту: состав камер меняет администратор, а не события в сети,
 * и перезапрашивать его при каждом переключении раскладки незачем.
 */
export function useCameras(enabled = true) {
  return useQuery({
    queryKey: camerasKey,
    enabled,
    staleTime: 60_000,
    queryFn: async () => (await apiRequest('/cameras', cameraListSchema)).items,
  });
}

export function useLayouts(enabled = true) {
  return useQuery({
    queryKey: layoutsKey,
    enabled,
    staleTime: 60_000,
    queryFn: async () => (await apiRequest('/layouts', layoutListSchema)).items,
  });
}

export function useSaveLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SaveLayout) =>
      apiRequest<CameraLayout>('/layouts', cameraLayoutSchema, { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: layoutsKey }),
  });
}

export function useDeleteLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (layoutId: string) =>
      apiRequest(`/layouts/${layoutId}`, z.unknown(), {
        method: 'DELETE',
        acceptStatuses: [204],
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: layoutsKey }),
  });
}

export function useCreateCamera() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCameraInput) =>
      apiRequest<Camera>('/cameras', cameraSchema, {
        method: 'POST',
        body: input,
        acceptStatuses: [201],
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: camerasKey }),
  });
}

export function useUpdateCamera(cameraId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateCamera) =>
      apiRequest<Camera>(`/cameras/${cameraId}`, cameraSchema, { method: 'PATCH', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: camerasKey }),
  });
}

export function useProbeCamera() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cameraId: string) =>
      apiRequest<CameraProbeResult>(`/cameras/${cameraId}/probe`, cameraProbeResultSchema, {
        method: 'POST',
        acceptStatuses: [201],
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: camerasKey }),
  });
}

const recorderListSchema = z.object({ items: z.array(recorderSchema) });

export const recordersKey = ['recorders'] as const;

/** Видеорегистраторы вместе с каналами (ADR 037). Живут столько же, сколько камеры. */
export function useRecorders(enabled = true) {
  return useQuery({
    queryKey: recordersKey,
    enabled,
    staleTime: 60_000,
    queryFn: async () => (await apiRequest('/recorders', recorderListSchema)).items,
  });
}

/**
 * Любая правка регистратора меняет и камеры: каналы — строки камер, и стена
 * со списком камер обязаны увидеть новые пути и имена без перезагрузки.
 */
function invalidateRecorders(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: recordersKey }),
    queryClient.invalidateQueries({ queryKey: camerasKey }),
  ]).then(() => undefined);
}

export function useCreateRecorder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateRecorderInput) =>
      apiRequest<Recorder>('/recorders', recorderSchema, {
        method: 'POST',
        body: input,
        acceptStatuses: [201],
      }),
    onSuccess: () => invalidateRecorders(queryClient),
  });
}

export function useUpdateRecorder(recorderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateRecorder) =>
      apiRequest<Recorder>(`/recorders/${recorderId}`, recorderSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => invalidateRecorders(queryClient),
  });
}

/**
 * Определение каналов. Неудача приходит заполненным ответом с причиной,
 * а не отказом запроса: её показывают рядом с кнопкой.
 */
export function useDetectChannels() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recorderId: string) =>
      apiRequest<RecorderDetectResult>(
        `/recorders/${recorderId}/detect`,
        recorderDetectResultSchema,
        { method: 'POST', acceptStatuses: [201] },
      ),
    onSuccess: () => invalidateRecorders(queryClient),
  });
}

/** Открыть поток: аренда зрителя, тикет и адреса транспортов. */
export function openStream(cameraId: string, quality: StreamQuality): Promise<CameraStream> {
  return apiRequest<CameraStream>(`/cameras/${cameraId}/stream`, cameraStreamSchema, {
    method: 'POST',
    body: { quality },
    acceptStatuses: [201],
  });
}

/** Продлить аренду: ячейка ещё открыта. */
export function keepaliveStream(leaseId: string): Promise<{ expiresAt: string }> {
  return apiRequest(`/cameras/leases/${leaseId}/keepalive`, streamKeepaliveSchema, {
    method: 'POST',
    acceptStatuses: [201],
  });
}

/** Новый тикет к той же аренде — для перехода на резервный транспорт. */
export function reissueTicket(leaseId: string): Promise<StreamTicketGrant> {
  return apiRequest<StreamTicketGrant>(
    `/cameras/leases/${leaseId}/ticket`,
    streamTicketGrantSchema,
    { method: 'POST', acceptStatuses: [201] },
  );
}

/**
 * Закрыть просмотр.
 *
 * `keepalive` обязателен: запрос уходит и при закрытии вкладки, а обычный
 * `fetch` браузер отменяет вместе с документом — и поток висел бы до
 * истечения аренды. `sendBeacon` здесь не годится: он не умеет ни метода
 * `DELETE`, ни заголовка с токеном.
 *
 * Отказы проглатываются намеренно: закрытие — операция «сделай, чтобы
 * не было», и её неудача ничего не меняет для того, кто уже ушёл. Аренда
 * истечёт сама, а поток погаснет по обходу.
 */
export async function closeStream(leaseId: string): Promise<void> {
  await fetch(`${API_BASE}/cameras/leases/${leaseId}`, {
    method: 'DELETE',
    keepalive: true,
    headers: authStore.token === null ? {} : { authorization: `Bearer ${authStore.token}` },
  }).catch(() => undefined);
}

/**
 * Обмен SDP с медиасервером через `api` (WHEP).
 *
 * Не через `apiRequest`: тело и ответ здесь не JSON, а SDP. Токен всё равно
 * нужен — тикет второй рубеж, а не замена входу.
 */
export async function exchangeSdp(
  cameraId: string,
  ticket: string,
  offer: string,
  signal: AbortSignal,
): Promise<string> {
  const response = await fetch(
    `${API_BASE}/cameras/${cameraId}/whep?ticket=${encodeURIComponent(ticket)}`,
    {
      method: 'POST',
      headers: {
        'content-type': 'application/sdp',
        ...(authStore.token === null ? {} : { authorization: `Bearer ${authStore.token}` }),
      },
      body: offer,
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(messageKey('video.mediaServerRejected', { status: response.status }));
  }

  return response.text();
}

/** Камера по идентификатору — для ячейки, которой пришёл только id. */
export function cameraById(cameras: Camera[] | undefined, id: string): Camera | undefined {
  return cameras?.find((camera) => camera.id === id);
}
